import React, { useEffect, useMemo, useState } from 'react';
import { Tour, CK_STYLES, CK_MODES } from '@NP-Webber/crm-kit/dist/next/index.js';
import useNextDesign from './useNextDesign';

/**
 * Guided tour of the new design: the layout (module, sidebar, settings, search, quick actions, related settings,
 * personal menu, tables) + a live step to try the style and display options.
 * Opens once per user on the first visit in the new design, and again from the personal menu.
 */
export const OPEN_TOUR_EVENT = 'bchinot:open-next-tour';
const KEY = (uid) => `bchinot-next-tour:${uid || 'anon'}`;

export function markTourPending(uid) {
  try { localStorage.removeItem(KEY(uid)); } catch { /* storage blocked */ }
}
export function openNextDesignTour() {
  window.dispatchEvent(new Event(OPEN_TOUR_EVENT));
}

function DesignOptions({ nextDesign }) {
  const styleMeta = CK_STYLES.find((s) => s.key === nextDesign.style) || CK_STYLES[0];
  return (
    <div className="nd-tour-opts">
      <p>הבחירה נשמרת אצלך ומופעלת מיד — נסו:</p>
      <div className="nd-tour-opts__row">
        <span>סגנון</span>
        <div className="ck-options" role="radiogroup" aria-label="סגנון עיצוב">
          {CK_STYLES.map((s) => (
            <button key={s.key} type="button" role="radio" aria-checked={nextDesign.style === s.key} className={`ck-option ${nextDesign.style === s.key ? 'is-on' : ''}`} onClick={() => nextDesign.setStyle(s.key)}>{s.label}</button>
          ))}
        </div>
      </div>
      <div className="nd-tour-opts__row">
        <span>תצוגה</span>
        <div className="ck-options" role="radiogroup" aria-label="מצב תצוגה">
          {CK_MODES.map((m) => (
            <button key={m.key} type="button" role="radio" aria-checked={nextDesign.mode === m.key} disabled={!styleMeta.supportsDark && m.key !== 'light'} className={`ck-option ${nextDesign.mode === m.key ? 'is-on' : ''}`} onClick={() => nextDesign.setMode(m.key)}>{m.label}</button>
          ))}
        </div>
      </div>
      {!styleMeta.supportsDark ? <p className="nd-tour-opts__hint">מצב כהה זמין בסגנון „מודרני”.</p> : null}
      <p className="nd-tour-opts__hint">אפשר לשנות בכל עת: התפריט האישי (בתחתית הסרגל) ← מצב תצוגה, או „הגדרות” בתחתית הסרגל.</p>
    </div>
  );
}

export default function NextDesignTour({ force = false } = {}) {
  const nextDesign = useNextDesign();
  const uid = nextDesign.userKey;
  const [open, setOpen] = useState(force);

  // first visit in the new design → open once the shell has rendered
  useEffect(() => {
    if (!nextDesign.enabled) return undefined;
    let seen = false;
    try { seen = localStorage.getItem(KEY(uid)) === 'done'; } catch { seen = true; }
    if (seen) return undefined;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [nextDesign.enabled, uid]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_TOUR_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TOUR_EVENT, onOpen);
  }, []);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(KEY(uid), 'done'); } catch { /* storage blocked */ }
  };

  const steps = useMemo(() => [
    {
      title: 'ברוכים הבאים לעיצוב החדש',
      wide: true,
      body: (
        <>
          <p>אותה מערכת, אותם נתונים — בפריסה חדשה, נקייה ומהירה יותר.</p>
          <p>בסיור קצר (דקה) נראה איפה נמצא כל דבר ונכיר את אפשרויות העיצוב.</p>
        </>
      ),
    },
    {
      target: '.ck-sidebar__nav',
      title: 'הסרגל — כל המסכים',
      body: (
        <ul>
          <li>המסכים מקובצים לפי נושא — אנשים, מבחנים, תוכן ותקשורת.</li>
          <li>כל ההגדרות (משתמשים, תפקידים, יומן ביקורת…) מרוכזות במקום אחד — <b>הגדרות</b> בתחתית.</li>
        </ul>
      ),
    },
    {
      target: '.ck-gsearch',
      optional: true,
      title: 'חיפוש מהיר — Ctrl+K',
      body: <p>מקלידים שם של חבר, כולל, מבחן, מסך או פעולה ומגיעים אליו מיד, מכל מקום במערכת.</p>,
    },
    {
      target: '.ck-qa',
      optional: true,
      title: '„חדש” ופעולות מהירות',
      body: <p>יצירה מהירה (חבר, הרשמה למבחן, כולל…) מכל מסך, גם במקלדת: Alt+1, Alt+2… אפשר לנעוץ כאן את הפעולות שמשתמשים בהן הכי הרבה.</p>,
    },
    {
      target: '.rs-btn',
      optional: true,
      title: 'הגדרות קשורות — Ctrl+,',
      body: <p>ההגדרות שמשפיעות על המסך שאתם נמצאים בו, בלחיצה אחת — וחזרה למסך בכפתור „חזרה ל…”.</p>,
    },
    {
      target: '.ck-dt__toolbar',
      optional: true,
      title: 'טבלאות',
      body: <p>בכל טבלה: חיפוש, סינון לפי כל עמודה (מהכותרת), בחירת עמודות ותצוגות שמורות, ייצוא לאקסל ופעולות על כמה שורות יחד.</p>,
    },
    {
      target: '.ck-sidebar__user',
      title: 'התפריט האישי',
      body: <p>אזור אישי, מצב תצוגה, חזרה לעיצוב הקודם — ומכאן אפשר לפתוח שוב את הסיור הזה.</p>,
    },
    {
      title: 'נסו את אפשרויות העיצוב',
      wide: true,
      body: <DesignOptions nextDesign={nextDesign} />,
    },
    {
      title: 'זהו, אפשר להתחיל',
      body: <p>בהצלחה! הסיור זמין תמיד בתפריט האישי ← „סיור בעיצוב החדש”.</p>,
    },
  ], [nextDesign]);

  if (!nextDesign.enabled && !force) return null;
  return <Tour open={open} steps={steps} onClose={close} />;
}
